import { updateMyProfileApi, useMe } from "@/api/services/main/auth";
import { Form } from "@/components/elements/Form";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { ERROR_STATUS_CODES } from "@/constants/error";
import { INVALID_ALREADY_EXISTS, INVALID_CURRENT_PASSWORD } from "@/constants/invalids";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { AccountBasicInfoFields } from "@/features/user-setting/components/edit/AccountBasicInfoFields";
import { SecuritySettingsEditSection } from "@/features/user-setting/components/edit/SecuritySettingsEditSection";
import { UserSettingEditFooter } from "@/features/user-setting/components/edit/UserSettingEditFooter";
import { buildUserSettingEditFormSchema } from "@/features/user-setting/schema/userSettingEditFormSchema";
import { TUserSettingEditForm } from "@/features/user-setting/types/userSettingEditForm";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { layout } from "@/theme/variables";
import { isApiError, setErrorForm, showError } from "@/utils/error";
import { replaceStr } from "@/utils/string";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useMemo } from "react";
import { Resolver, useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";

export const UserSettingEditPage: FC = () => {
  const { data, isLoading } = useMe();
  const me = data?.data;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const hasPassword = me?.isPasswordRegistered ?? true;
  const schema = useMemo(() => buildUserSettingEditFormSchema(hasPassword), [hasPassword]);

  const methods = useForm<TUserSettingEditForm>({
    mode: "onChange",
    // Schema is built dynamically from hasPassword, so its inferred type diverges from the fixed form type.
    resolver: yupResolver(schema) as Resolver<TUserSettingEditForm>,
    defaultValues: { fullName: "", currentPassword: "", newPassword: "", confirmNewPassword: "" },
  });
  const { control, reset } = methods;

  useEffect(() => {
    if (!me) return;
    reset({ fullName: me.fullName, currentPassword: "", newPassword: "", confirmNewPassword: "" });
  }, [me, reset]);

  const onSubmit = async (form: TUserSettingEditForm) => {
    try {
      await updateMyProfileApi({
        fullName: form.fullName,
        ...(form.newPassword && {
          newPassword: form.newPassword,
          currentPassword: hasPassword ? form.currentPassword : undefined,
        }),
      });
      // Registering a password flips isPasswordRegistered, which re-enables 現在のパスワード; clear the
      // password fields so the refreshed form never keeps the just-submitted values.
      methods.reset({ fullName: form.fullName, currentPassword: "", newPassword: "", confirmNewPassword: "" });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER.FETCH_ME] });
      toast.success("ユーザー設定を更新しました。");
      navigate(AppRoutes.userSettingDetail);
    } catch (error) {
      if (isApiError(error) && error.code === ERROR_STATUS_CODES.FULLNAME_HAS_BEEN_ALREADY_EXISTS) {
        methods.setError("fullName", { message: replaceStr(INVALID_ALREADY_EXISTS, { path: "氏名" }) });
      } else if (isApiError(error) && error.code === ERROR_STATUS_CODES.INCORRECT_PASSWORD) {
        methods.setError("currentPassword", { message: INVALID_CURRENT_PASSWORD });
      } else {
        showError(error);
      }
      setErrorForm(methods, error);
    }
  };

  if (isLoading) return <LoadingArea height={400} />;
  if (!me) return <Navigate to={AppRoutes.pageNotFound} replace />;

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={schema}>
      <Stack gap="24px" minHeight="calc(100vh - 105px)">
        <Stack width="100%" alignItems="center">
          <Stack width={layout.pageContentWidth} maxWidth="100%" gap="4px">
            <Typography variant="head20Bold" color={text.primary}>
              ユーザー設定の編集
            </Typography>
            <Typography variant="body14" color={text.secondary}>
              アカウント基本情報、およびセキュリティ設定を編集中です。一部の項目は保存またはページを戻ってから変更してください。
            </Typography>
          </Stack>
        </Stack>

        <Stack flex={1} width="100%" alignItems="center">
          <Box width={layout.pageContentWidth} maxWidth="100%">
            <MenuNavigate
              contents={[
                { title: "アカウント基本情報", content: <AccountBasicInfoFields control={control} me={me} /> },
                {
                  title: "セキュリティ設定",
                  content: (
                    <SecuritySettingsEditSection
                      control={control}
                      googleConnected={me.isGoogleLinked}
                      googleEmail={me.email}
                      hasPassword={hasPassword}
                    />
                  ),
                },
              ]}
            />
          </Box>
        </Stack>

        <Stack width="100%" alignItems="center" sx={{ position: "sticky", bottom: 0, zIndex: 1 }}>
          <Box width={layout.pageContentWidth} maxWidth="100%">
            <Box pl={layout.menuNavigateOffset}>
              <UserSettingEditFooter />
            </Box>
          </Box>
        </Stack>
      </Stack>
    </Form>
  );
};
